import React, { useEffect, useMemo, useRef, useState } from "react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { openWhatsAppOrCall } from "../../utils/directBooking";

function DirectBookingCTA() {
  const [isExpanded, setIsExpanded] = useState(true);
  const [checkInDate, setCheckInDate] = useState("");
  const [checkOutDate, setCheckOutDate] = useState("");
  const [error, setError] = useState("");
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setIsExpanded(false);
    }, 7000);

    return () => window.clearTimeout(timer);
  }, []);

  useEffect((): void | (() => void) => {
    if (!isExpanded) {
      return undefined;
    }

    const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
      const targetNode = event.target as Node | null;
      if (
        containerRef.current &&
        targetNode &&
        !containerRef.current.contains(targetNode)
      ) {
        setIsExpanded(false);
      }
    };

    const handleScroll = () => {
      setIsExpanded(false);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isExpanded]);

  const minCheckOut = useMemo(() => {
    return checkInDate || undefined;
  }, [checkInDate]);

  const buildBookingMessage = () => {
    return [
      "Hi Nutmeg Grove, I would like to book directly on WhatsApp for the best available price.",
      `Check-in: ${checkInDate}`,
      `Check-out: ${checkOutDate}`,
      "Please share your best direct booking rate and availability.",
    ].join("\n");
  };

  const handleDirectBooking = () => {
    if (!checkInDate || !checkOutDate) {
      setError("Please select both check-in and check-out dates.");
      return;
    }

    if (checkOutDate <= checkInDate) {
      setError("Check-out date must be after check-in date.");
      return;
    }

    setError("");
    openWhatsAppOrCall(buildBookingMessage());
  };

  if (!isExpanded) {
    return (
      <div className="fixed bottom-4 right-3 z-[9999] md:right-6">
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="rounded-full bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-2xl p-3 hover:from-green-500 hover:to-emerald-400 transition-all duration-300"
          aria-label="Open direct WhatsApp booking"
          title="Direct WhatsApp Booking"
        >
          <WhatsAppIcon />
        </button>
      </div>
    );
  }

  return (
    <div className="fixed bottom-4 right-3 left-auto w-[calc(100%-1.5rem)] sm:w-auto sm:max-w-[360px] md:right-6 md:max-w-[420px] z-[9999]">
      <div
        ref={containerRef}
        className="w-full rounded-2xl border border-green-200 bg-gradient-to-r from-green-600 to-emerald-500 text-white shadow-2xl px-4 py-3 transition-all duration-300"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-white/20 p-2">
              <WhatsAppIcon fontSize="small" />
            </span>
            <div>
              <p className="text-sm md:text-base font-bold leading-tight">
                Direct WhatsApp Booking
              </p>
              <p className="text-xs md:text-sm text-green-50 leading-tight mt-0.5">
                Best Price Guaranteed - Tap to Chat Instantly
              </p>
            </div>
          </div>
          <span className="shrink-0 rounded-full bg-white/20 px-2 py-1 text-[11px] font-semibold">
            24/7
          </span>
        </div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-xs text-green-50">
            Check-in
            <input
              type="date"
              value={checkInDate}
              onChange={(event) => {
                setCheckInDate(event.target.value);
                setError("");
              }}
              className="mt-1 w-full rounded-md border border-white/30 bg-white/95 px-2 py-1.5 text-sm text-secondary-main focus:outline-none focus:ring-2 focus:ring-green-200"
            />
          </label>
          <label className="text-xs text-green-50">
            Check-out
            <input
              type="date"
              min={minCheckOut}
              value={checkOutDate}
              onChange={(event) => {
                setCheckOutDate(event.target.value);
                setError("");
              }}
              className="mt-1 w-full rounded-md border border-white/30 bg-white/95 px-2 py-1.5 text-sm text-secondary-main focus:outline-none focus:ring-2 focus:ring-green-200"
            />
          </label>
        </div>

        {error && <p className="mt-2 text-xs text-red-100">{error}</p>}

        <button
          type="button"
          onClick={handleDirectBooking}
          className="mt-3 w-full rounded-lg bg-white text-green-700 font-semibold py-2 hover:bg-green-50 transition-colors"
          aria-label="Send booking request on WhatsApp"
        >
          Check Availability on WhatsApp
        </button>

        <div className="mt-2 flex items-center justify-center gap-2 text-[12px] text-green-50/95">
          {/* <LocalPhoneIcon sx={{ fontSize: 14 }} /> */}
          <span>(Auto-calls if WhatsApp is unavailable)</span>
        </div>

        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          className="mt-2 text-[11px] text-green-50/90 underline"
        >
          Minimize
        </button>
      </div>
    </div>
  );
}

export default DirectBookingCTA;
